import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFeature } from 'flagged';
import BasicPageTitle from '@/components/common/BasicPageTitle';
import FilterButton from '@/components/common/FilterButton';
import SearchInput from '@/components/common/SearchInput';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useMobileView from '@/hooks/ui/useMobileView';
import useAdvancedConstituentSearch from '@/lib/hooks/swr/useAdvancedConstituentSearch';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { BulkActionStatus, BulkEditActions } from '@/modules/BulkEdit';
import ConstituentExport from '@/modules/ConstituentExport';
import type { ConstituentSearchFilterParams } from '@/modules/ConstituentSearch';
import {
  ConstituentColumnSelect,
  FilterList,
} from '@/modules/ConstituentSearch';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import ConstituentSearchDrawer from '@/modules/ConstituentSearch/components/ConstituentSearchDrawer';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import QuickFilters from '@/modules/ConstituentSearch/components/QuickFilters';
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line no-restricted-imports
import useConstituentSearchFilter from '@/modules/ConstituentSearch/hooks/useConstituentSearchFilter';
import { useQueryProvider } from '@/modules/QueryProvider';
import type AdvancedConstituentSearchParams from '@/types/AdvancedConstituentSearchParams';
import laggy from '@/utils/swr/laggy';

type ConstituentListViewHeaderProps = {
  searchParams: AdvancedConstituentSearchParams;
  excludedItems: string[];
  selectedItems: string[];
  setExcludedItems: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
};

function ConstituentListViewHeader({
  searchParams,
  excludedItems,
  selectedItems,
  setExcludedItems,
  setSelectedItems,
}: ConstituentListViewHeaderProps) {
  const [isFilterModalActive, setIsFilterModalActive] = React.useState(false);
  const { query } = useQueryProvider<ConstituentSearchFilterParams>();
  const { data } = useAdvancedConstituentSearch(searchParams, { use: [laggy] });
  const { updateFilters } = useConstituentSearchFilter();
  const { control } = useFormContext();
  const isMobile = useMobileView();
  const isMediumView = useMobileView('lg');
  const hasColumnSelection = useFeature(
    FeatureFlags.CONSTITUENT_COLUMN_SELECTION,
  );

  return (
    <div className="mb-4 grid gap-4">
      <div className="w-full md:flex md:justify-between">
        <BasicPageTitle title={translate('CONSTITUENTS')} />
        <div className="flex gap-2">
          <Controller
            control={control}
            name={ConstituentSearchFilterField.NAME_EMAIL_PHONE}
            render={({ field: { onChange, value } }) => (
              <SearchInput
                id="constituent-search-name-email-phone"
                backgroundColor="#fff"
                onChange={(val: string) => {
                  onChange(val);
                  updateFilters();
                }}
                className="md:w-80 lg:w-125"
                label={translate('CONSTITUENT_SEARCH')}
                placeholder={translate('CONSTITUENT_SEARCH_BY')}
                value={value}
                disabled={!!selectedItems.length}
              />
            )}
          />
          {isMobile && (
            <>
              <FilterButton
                type="button"
                variant="contained"
                color="primary"
                onClick={() => setIsFilterModalActive(true)}
                size="small"
                data-testid="constituent-filter-button"
              />
              <ConstituentExport
                className="flex-shrink-0"
                totalConstituents={data?.totalHits || 0}
              />
            </>
          )}
        </div>
      </div>
      <BulkActionStatus className="mb-4" />
      {selectedItems.length ? (
        <BulkEditActions
          allConstituentsCount={data?.totalHits}
          clearSelectedItems={() => {
            setSelectedItems([]);
            setExcludedItems([]);
          }}
          constituents={data?.constituents}
          excludedItems={excludedItems}
          query={[{ ...query }]}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      ) : (
        <div className="flex w-full items-center justify-between md:h-[62px]">
          <h2 className="type-h3">{`${translate('ALL_CONSTITUENTS')} (${
            data?.totalHits ?? '-'
          })`}</h2>
          {!isMobile && (
            <div className="flex gap-6">
              {hasColumnSelection && <ConstituentColumnSelect />}
              {!isMediumView && <QuickFilters />}
              <FilterButton
                className="md:h-12 md:px-6"
                type="button"
                variant="contained"
                color="primary"
                onClick={() => setIsFilterModalActive(true)}
                size="small"
                data-testid="constituent-filter-button"
              />
              <ConstituentExport
                className="flex-shrink-0 md:h-12 md:px-6"
                totalConstituents={data?.totalHits || 0}
              />
            </div>
          )}
        </div>
      )}
      <FilterList enableSavedFilters disabled={!!selectedItems.length} />
      <ConstituentSearchDrawer
        enableSavedFilters
        onClose={() => setIsFilterModalActive(false)}
        open={isFilterModalActive}
      />
    </div>
  );
}

export default ConstituentListViewHeader;
